import { Pagination, Stack } from '@landler/component-library';
import i18next from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { Claim, UpliftUnitDimension } from '@/api/rest/resources/types/organization';
import { DefaultWidgetNames, WidgetType } from '@/api/rest/resources/types/widget';
import { GenericTable } from '@/components/widgets/o/GenericTable/GenericTable';
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { useOrganization } from '@/hooks/useOrganization';
import { useWidgetSlotConfigurationByNameType } from '@/hooks/useWidgetConfigurationByOrganizationId';
import { useOrganizationClaims } from '@/pages/shared/hooks/useOrganizationClaims';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { MetadataColumn } from '@/utils/table/mergeColumns';

import { PortfolioPageLayout } from '../../components/PortfolioPageLayout';

export const UpliftUnitsPage = () => {
  const { t } = useTranslation();

  const defaultWidgetSlotsConfiguration = {
    name: DefaultWidgetNames.default_template_units,
    type: WidgetType.t_page,
    widgets: [
      {
        name: 'org_summary_row',
        type: WidgetType.a_stack,
        visible: true,
        relatedData: {
          direction: 'row',
          center: true,
          spacing: 6,
          className: 'flex-wrap lg:flex-nowrap items-stretch',
        },
        widgets: [
          {
            name: DefaultWidgetNames.default_total_account_value_card,
            type: WidgetType.o_total_account_value,
            visible: true,
          },
          {
            name: DefaultWidgetNames.default_stewarded_value_card,
            type: WidgetType.o_stewarded_value,
            visible: true,
          },
          {
            name: DefaultWidgetNames.default_ecological_integrity_card,
            type: WidgetType.o_ecological_integrity_index,
            visible: true,
          },
        ],
      },
    ],
  };

  const customWidgetSlotsConfiguration = useWidgetSlotConfigurationByNameType(
    DefaultWidgetNames.default_template_units,
    WidgetType.t_page,
  );

  const widgetSlotsConfiguration = { ...defaultWidgetSlotsConfiguration, ...customWidgetSlotsConfiguration };

  return (
    <PortfolioPageLayout title={t('navbar.units')} showFilters={true}>
      <WidgetSlotRenderer nestedWidget={widgetSlotsConfiguration} />
      <UnitsTable />
    </PortfolioPageLayout>
  );
};

const PAGE_SIZE = 10;

export const UnitsTable = () => {
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const setCurrentPage = (page: number) => setSearchParams({ page: `${page}` }, { preventScrollReset: true });

  const organizationId = useOrganizationId();
  const organization = useOrganization({ id: organizationId }).data.results[0];
  const paginatedClaims = useOrganizationClaims(PAGE_SIZE, (currentPage - 1) * PAGE_SIZE);

  const { count: totalClaimsCount, results: claims } = paginatedClaims.data;

  const isAllUnitsOwnedByOrganization = useMemo(() => {
    return claims.every((claim) => claim.investor_name === organization?.name);
  }, [claims, organization?.name]);

  const defaultColumns = useMemo<MetadataColumn<Claim>[]>(() => {
    const getHeader = (
      accessorKey:
        | 'dimension'
        | 'plot_name'
        | 'landsteward_name'
        | 'investor_name'
        | 'issued_at'
        | 'expires_at'
        | 'rdi'
        | 'issuance_level'
        | 'uplift_unit_id',
    ) => (
      <span className='typography-overline max-w-fit whitespace-nowrap leading-4'>
        {t(`shared.uplift.units.list.headers.${accessorKey}.title`)}
      </span>
    );
    const getDimension = (dimension: UpliftUnitDimension) => t(`shared.uplift.dimension.${dimension}`);

    return [
      {
        accessorKey: 'dimension',
        header: () => getHeader('dimension'),
        cell: ({ getValue }) => <span>{getDimension(getValue() as UpliftUnitDimension)}</span>,
      },
      {
        accessorKey: 'plot_name',
        header: () => getHeader('plot_name'),
        cell: ({ getValue }) => <span>{getValue() as string}</span>,
      },
      {
        accessorKey: 'issued_at',
        header: () => getHeader('issued_at'),
        cell: ({ getValue }) => <span>{new Date(getValue() as string).toLocaleDateString(i18next.language)}</span>,
      },
      {
        accessorKey: 'expires_at',
        header: () => getHeader('expires_at'),
        cell: ({ getValue }) => <span>{new Date(getValue() as string).toLocaleDateString(i18next.language)}</span>,
      },
      {
        accessorKey: 'rdi',
        header: () => getHeader('rdi'),
        cell: ({ getValue }) => <span>{getDisplayNumber(getValue() as string, i18next.language)}</span>,
      },
      {
        accessorKey: 'issuance_level',
        header: () => getHeader('issuance_level'),
        cell: ({ getValue }) => <span>{getDisplayNumber(getValue() as string, i18next.language)}</span>,
      },
      ...(!isAllUnitsOwnedByOrganization
        ? [
            {
              accessorKey: 'investor_name',
              header: () => getHeader('investor_name'),
              cell: ({ getValue }: { getValue: () => unknown }) => {
                const investorName = getValue() as string;
                const organizationName = organization?.name || '';
                return <span>{investorName !== organizationName ? investorName : ''}</span>;
              },
            },
          ]
        : []),
      {
        accessorKey: 'landsteward_name',
        header: () => getHeader('landsteward_name'),
        cell: ({ getValue }) => <span>{getValue() as string}</span>,
      },
      {
        accessorKey: 'uplift_unit_id',
        header: () => getHeader('uplift_unit_id'),
        cell: ({ getValue }) => <span>{getValue() as string}</span>,
      },
    ];
  }, [t, isAllUnitsOwnedByOrganization, organization?.name]);

  const tooltipContent = {
    dimension: t('shared.uplift.units.list.headers.dimension.tooltip.body'),
    plot_name: t('shared.uplift.units.list.headers.plot_name.tooltip.body'),
    investor_name: t('shared.uplift.units.list.headers.investor_name.tooltip.body'),
    landsteward_name: t('shared.uplift.units.list.headers.landsteward_name.tooltip.body'),
    issued_at: t('shared.uplift.units.list.headers.issued_at.tooltip.body'),
    expires_at: t('shared.uplift.units.list.headers.expires_at.tooltip.body'),
    rdi: t('shared.uplift.units.list.headers.rdi.tooltip.body'),
    issuance_level: t('shared.uplift.units.list.headers.issuance_level.tooltip.body'),
    uplift_unit_id: t('shared.uplift.units.list.headers.uplift_unit_id.tooltip.body'),
  };

  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageEnd = pageStart + PAGE_SIZE;

  return (
    <Stack spacing={8} data-testid='plot-list-desktop' className='w-full overflow-hidden scrollbar-hidden'>
      <span className='typography-overline'>
        {t('shared.uplift.units.pagination.title', {
          count: totalClaimsCount,
          start: totalClaimsCount > 0 ? Math.max(1, pageStart + 1) : 0,
          end: Math.min(pageEnd, totalClaimsCount),
        })}
      </span>
      <GenericTable
        data={claims}
        pageSize={PAGE_SIZE}
        defaultColumns={defaultColumns}
        showTooltips={true}
        tooltipContent={tooltipContent}
      />
      <Pagination
        className='typography-overline text-text-secondary mx-auto'
        currentPage={currentPage}
        totalCount={totalClaimsCount}
        pageSize={PAGE_SIZE}
        onPageChange={(nextPage) => {
          setCurrentPage(nextPage);
        }}
        data-testid='pagination'
      />
    </Stack>
  );
};
